import React from 'react';
import s from '../DietPlanBuilder.module.css';
import { PageHead } from '../BuilderShell';
import { Field } from '../ui';
import { copy } from '../../copy';
import { fmtDate, fmtInt } from '../../lib/format';
import { MACRO_KEYS, MACRO_META, type GoalsStepModel } from './goalsStep.shared';
import { MacroFoodIcon } from '../MacroFoodIcon';

export function GoalsStepDesktop({ g }: { g: GoalsStepModel }) {
  return (
    <div className={s.goalsPage}>
      <PageHead
        title="Set the daily goals"
        sub="Start with calories. The macros follow from the split, and you can change any number."
      />

      <div className={s.datesBlock}>
        <div className={s.datesGrid}>
          <Field label={copy.goals.name}>
            <input className={s.input} value={g.name} onChange={e => g.setName(e.target.value)} />
          </Field>
          <Field label={copy.goals.starts}>
            <input className={s.input} type="date" value={g.from} onChange={e => g.setFrom(e.target.value)} />
          </Field>
          <Field label={copy.goals.ends}>
            <input className={s.input} type="date" value={g.to} min={g.from} onChange={e => g.setTo(e.target.value)} />
          </Field>
        </div>
        {g.overlap && (
          <div className={s.macroSumBanner} data-warn role="alert">
            <span>
              {copy.goals.overlap(g.overlap.name, fmtDate(g.overlap.from), fmtDate(g.overlap.to))}
            </span>
            <button type="button" className={s.overlapFixBtn} onClick={g.applySuggestedDates}>
              {copy.goals.useFreeDates(fmtDate(g.suggestedRange.from), fmtDate(g.suggestedRange.to))}
            </button>
          </div>
        )}
      </div>

      <div className={s.goalsSplitRow}>
        <div className={s.goalsCard}>
          <span className={s.eyebrow}>Daily calorie goal</span>
          <div className={s.kcalStepper}>
            <button type="button" className={s.btnOutlineGreen} onClick={() => g.stepKcal(-50)} aria-label="Less">−</button>
            <input
              className={s.kcalInput}
              type="number"
              inputMode="numeric"
              value={g.kcal}
              onChange={e => g.setKcal(Number(e.target.value))}
              aria-label="Daily calories"
            />
            <button type="button" className={s.btnOutlineGreen} onClick={() => g.stepKcal(50)} aria-label="More">+</button>
          </div>
          <div className={s.kcalHint}>kcal per day</div>
        </div>

        <div className={s.goalsCardWide}>
          <div className={s.macroCardHead}>
            <span className={s.eyebrow}>Macronutrient split</span>
            <div className={s.macroPresetRow}>
              <span className={s.macroPresetHint}>Quick split, P/C/F:</span>
              {g.presets.map(pr => (
                <button
                  type="button"
                  key={pr.label}
                  className={s.chipPreset}
                  data-on={pr.active}
                  aria-pressed={pr.active}
                  onClick={() => g.applyPreset(pr.pct, pr.label)}
                >
                  {pr.chipLabel}
                </button>
              ))}
            </div>
          </div>

          <div className={s.macroBarV4} aria-hidden>
            {MACRO_KEYS.map(k => (
              <span
                key={k}
                className={s.macroSeg}
                style={{ width: `${g.pct[k]}%`, background: MACRO_META[k].bg, color: MACRO_META[k].fg }}
                title={MACRO_META[k].label}
              >
                {g.pct[k] >= 12 && (
                  <span style={{ color: '#fff', display: 'inline-flex' }}>
                    <MacroFoodIcon k={k} size={12} />
                  </span>
                )}
                {g.pct[k] >= 10 ? `${g.pct[k]}%` : ''}
              </span>
            ))}
          </div>

          <div className={s.macroGrid}>
            <span />
            <span className={`${s.macroGridHead} ${s.macroGridHeadCenter}`}>% of kcal</span>
            <span className={`${s.macroGridHead} ${s.macroGridHeadCenter}`}>Grams / day</span>
            <span className={`${s.macroGridHead} ${s.macroGridHeadEnd}`}>Calories</span>

            {MACRO_KEYS.map(k => {
              const m = MACRO_META[k];
              return (
                <React.Fragment key={k}>
                  <div className={s.macroRowLabel}>
                    <span className={s.macroIcon} style={{ background: m.bg }} aria-hidden>
                      <MacroFoodIcon k={k} size={16} />
                    </span>
                    <div className={s.macroRowText}>
                      <span className={s.macroRowName}>{m.label}</span>
                      <span className={s.macroRowSub}>{m.kcalPerG} kcal per gram</span>
                    </div>
                  </div>
                  <div className={s.macroField}>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={g.pct[k]}
                      onChange={e => g.setPct(k, +e.target.value)}
                      aria-label={`${m.label} percent`}
                    />
                    <span className={s.macroFieldUnit}>%</span>
                  </div>
                  <div className={s.macroField}>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={g.grams[k]}
                      onChange={e => g.setGrams(k, +e.target.value)}
                      aria-label={`${m.label} grams per day`}
                    />
                    <span className={s.macroFieldUnit}>g</span>
                  </div>
                  <span className={s.macroKcalCell}>{fmtInt(g.macroKcal[k])} kcal</span>
                </React.Fragment>
              );
            })}
          </div>

          {g.isBalanced ? (
            <div className={s.macroSumBanner}>
              Adds up to 100%: {g.grams.p} g protein, {g.grams.c} g carbs, {g.grams.f} g fats a day.
            </div>
          ) : (
            <div className={s.macroSumBanner} data-warn role="alert">
              <span>Adds up to {g.sumPct}%. It needs to be 100%.</span>
              {g.suggestion && (
                <button type="button" className={s.macroFixBtn} onClick={g.applySuggestion}>
                  {g.suggestion.delta > 0
                    ? `Add ${g.suggestion.delta}% across all macros`
                    : `Remove ${-g.suggestion.delta}% across all macros`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
